import { jsPDF } from 'jspdf';

const COLOR_PRIMARIO = [32, 69, 61];        // #20453D
const COLOR_SECUNDARIO = [57, 128, 106];    // #39806A
const COLOR_TEXTO = [55, 65, 81];           // gray-700
const COLOR_TEXTO_SUAVE = [148, 156, 165];  // gray-400
const COLOR_ENCABEZADO_TABLA = [243, 244, 246]; // gray-100
const COLOR_FILA_ALTERNA = [250, 250, 250];
const COLOR_SEPARADOR_COLUMNA = [221, 225, 229]; // gray-300

const ETIQUETAS_TIPO = {
  factura: 'Factura',
  pago: 'Pago',
  nota_credito: 'Nota de Crédito'
};

function crearGeometria() {
  const MARGEN = 28;
  const ANCHO_PAGINA = 595.28; // A4 en puntos
  const ALTO_PAGINA = 841.89;
  return {
    MARGEN,
    ANCHO_PAGINA,
    ALTO_PAGINA,
    ANCHO_UTIL: ANCHO_PAGINA - MARGEN * 2,
    ALTO_FOOTER: 26
  };
}

function formatMonto(monto) {
  return parseFloat(monto || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(fecha) {
  if (!fecha) return '-';
  return new Date(fecha).toLocaleDateString('es-DO', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatFechaCorta(fecha) {
  if (!fecha) return '-';
  return new Date(fecha).toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function asegurarEspacio(doc, geo, estado, y, alturaNecesaria) {
  if (y + alturaNecesaria > geo.ALTO_PAGINA - geo.ALTO_FOOTER) {
    doc.addPage();
    estado.pagina += 1;
    return geo.MARGEN;
  }
  return y;
}

function dibujarEncabezado(doc, geo, cliente, periodo) {
  doc.setFillColor(...COLOR_SECUNDARIO);
  doc.rect(0, 0, geo.ANCHO_PAGINA, 5, 'F');

  doc.setTextColor(...COLOR_PRIMARIO);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.text('Estado de Cuenta de Cliente', geo.MARGEN, 40);

  doc.setFontSize(12);
  doc.text(cliente.razon_social, geo.MARGEN, 58);

  doc.setTextColor(...COLOR_TEXTO_SUAVE);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const rango = periodo.fecha_desde || periodo.fecha_hasta
    ? `${periodo.fecha_desde ? formatFecha(periodo.fecha_desde) : 'Inicio'} - ${periodo.fecha_hasta ? formatFecha(periodo.fecha_hasta) : 'Hoy'}`
    : 'Histórico completo';
  const notaComprobante = periodo.solo_con_comprobante ? ' · Solo con comprobante fiscal (NCF)' : '';
  doc.text(`${rango}${notaComprobante}`, geo.MARGEN, 72);

  const generado = new Date().toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' });
  doc.text(`Generado: ${generado}`, geo.ANCHO_PAGINA - geo.MARGEN, 72, { align: 'right' });

  doc.setDrawColor(...COLOR_ENCABEZADO_TABLA);
  doc.setLineWidth(1);
  doc.line(geo.MARGEN, 84, geo.ANCHO_PAGINA - geo.MARGEN, 84);

  return 106;
}

function dibujarTituloSeccion(doc, geo, estado, y, texto) {
  const yy = asegurarEspacio(doc, geo, estado, y, 26);
  doc.setTextColor(...COLOR_PRIMARIO);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(texto, geo.MARGEN, yy);
  return yy + 14;
}

const TABLA_PADDING_X = 6;
const TABLA_PADDING_Y = 6;
const TABLA_LINE_HEIGHT = 10;

function partirEnLineas(doc, fontSize, valor, anchoColumna) {
  doc.setFontSize(fontSize);
  const anchoDisponible = Math.max(anchoColumna - TABLA_PADDING_X * 2, 12);
  return doc.splitTextToSize(String(valor ?? ''), anchoDisponible);
}

function dibujarSeparadoresColumna(doc, geo, columnas, yPos, altura) {
  doc.setDrawColor(...COLOR_SEPARADOR_COLUMNA);
  doc.setLineWidth(0.5);
  let x = geo.MARGEN;
  columnas.forEach((col, idx) => {
    x += col.width;
    if (idx < columnas.length - 1) {
      doc.line(x, yPos, x, yPos + altura);
    }
  });
}

function dibujarTabla(doc, geo, estado, { y, columnas, filas }) {
  const dibujarEncabezadoTabla = (yPos) => {
    doc.setFont('helvetica', 'bold');
    const lineasPorColumna = columnas.map(col => partirEnLineas(doc, 8, col.titulo.toUpperCase(), col.width));
    const maxLineas = Math.max(...lineasPorColumna.map(l => l.length), 1);
    const alturaEncabezado = maxLineas * TABLA_LINE_HEIGHT + TABLA_PADDING_Y;

    doc.setFillColor(...COLOR_ENCABEZADO_TABLA);
    doc.rect(geo.MARGEN, yPos, geo.ANCHO_UTIL, alturaEncabezado, 'F');
    doc.setTextColor(...COLOR_TEXTO);

    let x = geo.MARGEN;
    columnas.forEach((col, i) => {
      const textX = col.align === 'right' ? x + col.width - TABLA_PADDING_X : x + TABLA_PADDING_X;
      lineasPorColumna[i].forEach((linea, li) => {
        doc.text(linea, textX, yPos + TABLA_PADDING_Y + (li + 1) * TABLA_LINE_HEIGHT - 2, {
          align: col.align === 'right' ? 'right' : 'left'
        });
      });
      x += col.width;
    });

    dibujarSeparadoresColumna(doc, geo, columnas, yPos, alturaEncabezado);
    return yPos + alturaEncabezado;
  };

  let cursorY = asegurarEspacio(doc, geo, estado, y, TABLA_LINE_HEIGHT * 2 + TABLA_PADDING_Y * 2);
  cursorY = dibujarEncabezadoTabla(cursorY);

  filas.forEach((fila, idx) => {
    doc.setFont('helvetica', 'normal');
    const lineasPorColumna = columnas.map((col, i) => partirEnLineas(doc, 9, fila[i], col.width));
    const maxLineas = Math.max(...lineasPorColumna.map(l => l.length), 1);
    const alturaFila = maxLineas * TABLA_LINE_HEIGHT + TABLA_PADDING_Y;

    const yAntes = cursorY;
    cursorY = asegurarEspacio(doc, geo, estado, cursorY, alturaFila);
    if (cursorY !== yAntes) {
      cursorY = dibujarEncabezadoTabla(cursorY);
      doc.setFont('helvetica', 'normal');
    }

    if (idx % 2 === 1) {
      doc.setFillColor(...COLOR_FILA_ALTERNA);
      doc.rect(geo.MARGEN, cursorY, geo.ANCHO_UTIL, alturaFila, 'F');
    }

    doc.setTextColor(...COLOR_TEXTO);
    doc.setFontSize(9);
    let x = geo.MARGEN;
    columnas.forEach((col, i) => {
      const textX = col.align === 'right' ? x + col.width - TABLA_PADDING_X : x + TABLA_PADDING_X;
      lineasPorColumna[i].forEach((linea, li) => {
        doc.text(linea, textX, cursorY + TABLA_PADDING_Y + (li + 1) * TABLA_LINE_HEIGHT - 2, {
          align: col.align === 'right' ? 'right' : 'left'
        });
      });
      x += col.width;
    });

    dibujarSeparadoresColumna(doc, geo, columnas, cursorY, alturaFila);

    cursorY += alturaFila;
  });

  doc.setDrawColor(...COLOR_ENCABEZADO_TABLA);
  doc.setLineWidth(1);
  doc.line(geo.MARGEN, cursorY, geo.MARGEN + geo.ANCHO_UTIL, cursorY);

  return cursorY + 20;
}

function dibujarPiePagina(doc, geo) {
  const totalPaginas = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPaginas; i++) {
    doc.setPage(i);
    doc.setTextColor(...COLOR_TEXTO_SUAVE);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text('OmniERP', geo.MARGEN, geo.ALTO_PAGINA - 14);
    doc.text(`Página ${i} de ${totalPaginas}`, geo.ANCHO_PAGINA - geo.MARGEN, geo.ALTO_PAGINA - 14, { align: 'right' });
  }
}

/**
 * Genera y descarga el PDF del estado de cuenta de un cliente a partir de
 * la respuesta de GET /api/reporte/estado-cuenta-cliente/:id_entidad_cliente
 * (ver c_reporte.js: ObtenerEstadoCuentaCliente).
 */
export default function generarPDFEstadoCuentaCliente(datos) {
  if (!datos) return;

  const geo = crearGeometria();
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const estado = { pagina: 1 };

  let y = dibujarEncabezado(doc, geo, datos.cliente, datos.periodo);

  // RESUMEN
  y = dibujarTituloSeccion(doc, geo, estado, y, 'Resumen');
  const r = datos.resumen;
  y = dibujarTabla(doc, geo, estado, {
    y,
    columnas: [
      { titulo: 'Indicador', width: geo.ANCHO_UTIL * 0.6, align: 'left' },
      { titulo: 'Valor', width: geo.ANCHO_UTIL * 0.4, align: 'right' }
    ],
    filas: [
      ['Total Facturado', `RD$ ${formatMonto(r.total_facturado)}`],
      ['Total Pagado', `RD$ ${formatMonto(r.total_pagado)}`],
      ['Total Notas de Crédito', `RD$ ${formatMonto(r.total_notas_credito)}`],
      ['Balance Pendiente', `RD$ ${formatMonto(r.total_pendiente)}`],
      ['Balance Vencido', `RD$ ${formatMonto(r.total_vencido)}`],
      ['Cantidad de Facturas', String(r.cantidad_facturas)],
      ['Cantidad de Pagos', String(r.cantidad_pagos)]
    ]
  });

  // ANTIGÜEDAD DE CARTERA
  if (datos.antiguedad_cartera.length > 0) {
    y = dibujarTituloSeccion(doc, geo, estado, y, 'Antigüedad de Cartera');
    y = dibujarTabla(doc, geo, estado, {
      y,
      columnas: [
        { titulo: 'Rango', width: geo.ANCHO_UTIL * 0.5, align: 'left' },
        { titulo: 'Facturas', width: geo.ANCHO_UTIL * 0.2, align: 'right' },
        { titulo: 'Monto', width: geo.ANCHO_UTIL * 0.3, align: 'right' }
      ],
      filas: datos.antiguedad_cartera.map(a => [a.rango, String(a.cantidad), `RD$ ${formatMonto(a.total)}`])
    });
  }

  // DETALLE DE MOVIMIENTOS
  y = dibujarTituloSeccion(doc, geo, estado, y, 'Detalle de Movimientos');
  y = dibujarTabla(doc, geo, estado, {
    y,
    columnas: [
      { titulo: 'Fecha', width: geo.ANCHO_UTIL * 0.12, align: 'left' },
      { titulo: 'Tipo', width: geo.ANCHO_UTIL * 0.16, align: 'left' },
      { titulo: 'Documento', width: geo.ANCHO_UTIL * 0.32, align: 'left' },
      { titulo: 'Cargo', width: geo.ANCHO_UTIL * 0.13, align: 'right' },
      { titulo: 'Abono', width: geo.ANCHO_UTIL * 0.13, align: 'right' },
      { titulo: 'Balance', width: geo.ANCHO_UTIL * 0.14, align: 'right' }
    ],
    filas: datos.movimientos.map(m => [
      formatFechaCorta(m.fecha),
      ETIQUETAS_TIPO[m.tipo] || m.tipo,
      m.documento,
      m.cargo > 0 ? `RD$ ${formatMonto(m.cargo)}` : '-',
      m.abono > 0 ? `RD$ ${formatMonto(m.abono)}` : '-',
      `RD$ ${formatMonto(m.balance)}`
    ])
  });

  dibujarPiePagina(doc, geo);

  const sufijo = datos.periodo.fecha_desde && datos.periodo.fecha_hasta
    ? `${datos.periodo.fecha_desde}_${datos.periodo.fecha_hasta}`
    : hoyISO();
  doc.save(`estado-cuenta_${datos.cliente.razon_social.replace(/\s+/g, '-')}_${sufijo}.pdf`);
}

function hoyISO() {
  return new Date().toISOString().split('T')[0];
}
